import { Audio } from "@remotion/media";
import { Html5Audio } from "remotion";
import {
  audioPreviewComponent,
  mediaPlaybackRate,
  mediaPreservePitch,
} from "../media-playback";
import {
  useRemotionLayerVolume,
  type AudioLayerRenderProps,
} from "./use-audio-layer-media";

/**
 * Layers an audio file (music bed or voiceover) into the composition.
 * `CompositionRoot` places it on the timeline via the same `Sequence`
 * wrapper it uses for every layer (`from` / `duration` in frames).
 *
 * Pairs with the `@remotion/media` video layer: both decode through Mediabunny
 * and schedule onto one shared AudioContext anchored to Remotion's timeline, so
 * they advance on a single clock. An HTML5 `<audio>` element instead runs its
 * own clock and is only corrected once it drifts past
 * `acceptableTimeShiftInSeconds` (0.45s), which is audible as desync under
 * load. Lambda renders keep the HTML5 tag (see `LAMBDA_MEDIA_OVERRIDE`).
 *
 * Rate-1 stays on Mediabunny. A non-1 `playbackRate` with pitch preserved
 * switches to Html5Audio: `@remotion/media` pitch-shifts with rate (Mediabunny
 * cannot time-stretch), while Html5Audio / Lambda ffmpeg `atempo` both
 * preserve pitch. Opting out (`preservePitch: false`) stays on Mediabunny so
 * pitch follows rate and preview stays on the video clock. Preview of sped,
 * pitch-preserved audio may drift vs picture under load.
 */
export function AudioLayer(props: AudioLayerRenderProps) {
  const volume = useRemotionLayerVolume(props);
  const rate = mediaPlaybackRate(props.playbackRate);
  const pitch = mediaPreservePitch(props.preservePitch);
  if (audioPreviewComponent(rate, pitch) === "html5") {
    return (
      <Html5Audio
        src={props.src}
        volume={volume}
        playbackRate={rate}
        preservePitch
      />
    );
  }
  return <Audio src={props.src} volume={volume} playbackRate={rate} />;
}
